import type { ThemeId, ThemePalette } from './theme';
import { deriveThemeColors, type ThemeColorSeed, type ThemeRuntimeColors } from './themeColorAlgorithm';

const CUSTOM_THEME_PROPERTIES = [
  '--fp-bg-page', '--fp-bg-chrome', '--fp-bg-nav', '--fp-bg-surface', '--fp-primary', '--fp-primary-soft',
  '--fp-interactive-hover-bg', '--fp-interactive-hover-text', '--fp-on-primary', '--fp-text', '--fp-text-muted',
  '--fp-danger', '--fp-success', '--fp-focus', '--fp-border', '--fp-surface-raised', '--fp-bg-surface-soft',
  '--fp-header-background', '--fp-header-text', '--fp-nav-background', '--fp-nav-text', '--fp-card-background',
  '--fp-card-border', '--fp-control-selected-background', '--fp-control-selected-text', '--fp-action-surface',
  '--fp-action-ink', '--fp-action-border', '--fp-action-hover', '--fp-action-hover-ink', '--fp-control-surface',
  '--fp-warning', '--fp-info', '--fp-accent', '--fp-color-scheme', '--fp-text-secondary', '--fp-text-placeholder',
  '--fp-control-icon', '--fp-control-border', '--fp-control-focus', '--fp-danger-base', '--fp-success-base',
  '--fp-success-soft', '--fp-danger-soft', '--fp-info-soft', '--fp-primary-soft-strong', '--fp-on-primary-soft',
  '--fp-on-primary-edge', '--fp-service-animation', '--fp-service-icon-style', '--fp-overlay-text', '--fp-link-color',
  '--fp-selection-bg', '--fp-selection-ink', '--fp-empty-icon-color', '--fp-error-icon-color', '--fp-loading-paw-glow',
  '--fp-notification-danger', '--fp-btn-danger-bg', '--fp-btn-danger-ink', '--fp-btn-danger-hover-bg',
  '--fp-btn-danger-active-bg', '--fp-text-on-danger', '--fp-letter-ink', '--fp-letter-muted', '--fp-letter-surface',
  '--fp-letter-line', '--fp-letter-paper-warm', '--fp-letter-paper-warm-edge', '--fp-letter-paper-warm-ink',
  '--fp-letter-paper-sepia', '--fp-letter-paper-sepia-ink', '--fp-letter-paper-summary-ink', '--fp-letter-paper-body-ink',
  '--fp-letter-paper-footer-ink', '--fp-letter-paper-time-ink', '--fp-letter-paper-header-ink', '--fp-letter-page-ink',
  '--fp-letter-paper-ink', '--fp-letter-paper-rule', '--fp-letter-paper-action-bg', '--fp-letter-paper-action-border',
  '--fp-letter-paper-action-ink', '--fp-letter-paper-editor-cancel-bg', '--fp-letter-paper-editor-cancel-border',
  '--fp-letter-paper-editor-cancel-ink', '--fp-letter-paper-editor-draft-bg', '--fp-letter-paper-editor-draft-border',
  '--fp-letter-paper-editor-draft-ink', '--fp-switch-off-start', '--fp-switch-off-end', '--fp-switch-off-edge',
  '--fp-switch-thumb', '--fp-switch-thumb-edge', '--fp-switch-on-start', '--fp-switch-on-end', '--fp-switch-on-edge',
  '--fp-page-background', '--fp-custom-background-image', '--fp-custom-background-overlay',
] as const;

const FALLBACK_CUSTOM_PALETTE: ThemePalette = [
  'var(--fp-theme-custom-fallback-background)',
  'var(--fp-theme-custom-fallback-surface)',
  'var(--fp-theme-custom-fallback-primary)',
];

const RUNTIME_SEED_TOKENS: Record<keyof ThemeColorSeed, string> = {
  white: '--fp-theme-runtime-white', black: '--fp-theme-runtime-black', fallbackText: '--fp-theme-runtime-fallback-text',
  dangerLight: '--fp-theme-runtime-danger-light', dangerDark: '--fp-theme-runtime-danger-dark',
  successLight: '--fp-theme-runtime-success-light', successDark: '--fp-theme-runtime-success-dark',
  warningLight: '--fp-theme-runtime-warning-light', warningDark: '--fp-theme-runtime-warning-dark',
};

function resolveCssColor(value: string): string {
  let resolved = value.trim();
  const seen = new Set<string>();
  while (resolved.startsWith('var(')) {
    const tokenName = resolved.slice(4, -1).trim();
    if (!tokenName || seen.has(tokenName)) return '';
    seen.add(tokenName);
    resolved = getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
  }
  return resolved;
}

function readRuntimeSeed(): ThemeColorSeed {
  return Object.fromEntries(Object.entries(RUNTIME_SEED_TOKENS).map(([key, token]) => [
    key, resolveCssColor(getComputedStyle(document.documentElement).getPropertyValue(token)),
  ])) as unknown as ThemeColorSeed;
}

function setRuntimeColors(rootStyle: CSSStyleDeclaration, colors: ThemeRuntimeColors): void {
  const values: Record<string, string> = {
    '--fp-bg-page': colors.background, '--fp-bg-chrome': colors.chrome, '--fp-bg-nav': colors.navigation,
    '--fp-bg-surface': colors.surface, '--fp-surface-raised': colors.surfaceRaised, '--fp-bg-surface-soft': colors.surfaceSoft,
    '--fp-header-background': colors.chrome, '--fp-header-text': colors.headerText, '--fp-nav-background': colors.navigation,
    '--fp-nav-text': colors.navText, '--fp-card-background': colors.surface, '--fp-card-border': colors.cardBorder,
    '--fp-control-selected-background': colors.primary, '--fp-control-selected-text': colors.primaryText,
    '--fp-border': colors.border, '--fp-primary': colors.primary, '--fp-primary-soft': colors.primarySoft,
    '--fp-switch-off-start': colors.switchOffStart, '--fp-switch-off-end': colors.switchOffEnd, '--fp-switch-off-edge': colors.switchOffEdge,
    '--fp-switch-thumb': colors.switchThumb, '--fp-switch-thumb-edge': colors.switchThumbEdge,
    '--fp-switch-on-start': colors.switchOnStart, '--fp-switch-on-end': colors.switchOnEnd, '--fp-switch-on-edge': colors.switchOnEdge,
    '--fp-interactive-hover-bg': colors.hoverSurface, '--fp-interactive-hover-text': colors.pageText,
    '--fp-on-primary': colors.primaryText, '--fp-text': colors.pageText, '--fp-text-muted': colors.mutedText,
    '--fp-text-secondary': colors.mutedText, '--fp-text-placeholder': colors.mutedText,
    '--fp-danger-base': colors.danger, '--fp-danger': colors.danger, '--fp-success-base': colors.success, '--fp-success': colors.success,
    '--fp-focus': colors.pageText, '--fp-action-surface': colors.primary, '--fp-action-ink': colors.primaryText,
    '--fp-action-border': colors.primary, '--fp-action-hover': `color-mix(in srgb, ${colors.primary} 82%, ${colors.primaryText})`,
    '--fp-action-hover-ink': colors.primaryText, '--fp-control-surface': colors.controlSurface, '--fp-control-icon': colors.controlIcon,
    '--fp-control-border': colors.border, '--fp-control-focus': colors.pageText, '--fp-warning': colors.warning,
    '--fp-info': colors.primary, '--fp-accent': colors.primary, '--fp-color-scheme': colors.colorScheme,
    '--fp-primary-soft-strong': colors.primarySoftStrong, '--fp-success-soft': `color-mix(in srgb, ${colors.success} 78%, ${colors.surface})`,
    '--fp-danger-soft': `color-mix(in srgb, ${colors.danger} 78%, ${colors.surface})`, '--fp-info-soft': `color-mix(in srgb, ${colors.primary} 78%, ${colors.surface})`,
    '--fp-on-primary-soft': `color-mix(in srgb, ${colors.primaryText} 80%, transparent)`, '--fp-on-primary-edge': `color-mix(in srgb, ${colors.primaryText} 36%, transparent)`,
    '--fp-service-animation': colors.primary, '--fp-service-icon-style': colors.primary, '--fp-overlay-text': colors.primaryText,
    '--fp-link-color': colors.linkColor, '--fp-selection-bg': colors.selectionBackground, '--fp-selection-ink': colors.primaryText,
    '--fp-empty-icon-color': colors.mutedText, '--fp-error-icon-color': colors.danger,
    '--fp-loading-paw-glow': `color-mix(in srgb, ${colors.primary} 45%, transparent)`, '--fp-notification-danger': colors.danger,
    '--fp-btn-danger-bg': colors.danger, '--fp-btn-danger-ink': colors.dangerInk, '--fp-btn-danger-hover-bg': colors.dangerHover,
    '--fp-btn-danger-active-bg': colors.dangerActive, '--fp-text-on-danger': colors.dangerInk,
    '--fp-letter-ink': colors.pageText, '--fp-letter-muted': colors.paperMuted, '--fp-letter-surface': colors.surface,
    '--fp-letter-line': colors.border, '--fp-letter-paper-warm': colors.paperSurface, '--fp-letter-paper-warm-edge': colors.paperEdge,
    '--fp-letter-paper-warm-ink': colors.paperInk, '--fp-letter-paper-sepia': colors.paperSepia, '--fp-letter-paper-sepia-ink': colors.paperSepiaInk,
    '--fp-letter-paper-summary-ink': colors.paperInk, '--fp-letter-paper-body-ink': colors.paperInk, '--fp-letter-paper-footer-ink': colors.paperMuted,
    '--fp-letter-paper-time-ink': colors.paperMuted, '--fp-letter-paper-header-ink': colors.paperInk, '--fp-letter-page-ink': colors.paperInk,
    '--fp-letter-paper-ink': colors.paperInk, '--fp-letter-paper-rule': colors.paperEdge, '--fp-letter-paper-action-bg': colors.paperSurface,
    '--fp-letter-paper-action-border': colors.paperEdge, '--fp-letter-paper-action-ink': colors.paperInk,
    '--fp-letter-paper-editor-cancel-bg': colors.paperSurface, '--fp-letter-paper-editor-cancel-border': colors.paperEdge,
    '--fp-letter-paper-editor-cancel-ink': colors.paperInk, '--fp-letter-paper-editor-draft-bg': colors.paperSepia,
    '--fp-letter-paper-editor-draft-border': colors.paperEdge, '--fp-letter-paper-editor-draft-ink': colors.paperSepiaInk,
  };
  Object.entries(values).forEach(([property, value]) => rootStyle.setProperty(property, value));
}

function clearCustomThemeVariables(rootStyle: CSSStyleDeclaration): void {
  CUSTOM_THEME_PROPERTIES.forEach((property) => rootStyle.removeProperty(property));
}

/** DOM 适配层：只负责读取 Token、调用纯算法并把语义结果写入根节点。 */
export function applyThemeRuntime(theme: ThemeId, customPalette: ThemePalette | null, customBackground: string | null): void {
  const root = document.documentElement;
  const rootStyle = root.style;
  root.dataset.theme = theme;
  const rawPalette = theme === 'custom' ? (customPalette ?? FALLBACK_CUSTOM_PALETTE) : null;
  const palette = rawPalette?.map(resolveCssColor) as ThemePalette | undefined;

  if (palette?.every((color) => /^#[0-9a-f]{6}$/i.test(color))) {
    setRuntimeColors(rootStyle, deriveThemeColors(palette, readRuntimeSeed()));
  } else {
    clearCustomThemeVariables(rootStyle);
  }

  if (theme === 'custom' && customBackground && palette?.[2]) {
    const seed = readRuntimeSeed();
    const overlay = deriveThemeColors(palette, seed).colorScheme === 'dark' ? seed.black : seed.white;
    rootStyle.setProperty('--fp-custom-background-overlay', `color-mix(in srgb, ${overlay} 42%, transparent)`);
    rootStyle.setProperty('--fp-custom-background-image', `url("${customBackground}")`);
    rootStyle.setProperty('--fp-page-background', 'linear-gradient(var(--fp-custom-background-overlay), var(--fp-custom-background-overlay))');
  } else {
    rootStyle.removeProperty('--fp-custom-background-image');
    rootStyle.removeProperty('--fp-custom-background-overlay');
    rootStyle.removeProperty('--fp-page-background');
  }
}
